import { StyleDeclarationValue, css } from 'aphrodite';
import { FC, useEffect, useState } from 'react';
import { useDebounceCallback } from '../../../../hooks/useDebounceValue';
import { useAdvancedFiltersContext } from '../../../components-v2/AdvancedFilters/AdvancedFiltersContext';
import { styleSheet } from './styles';

interface LocationFiltersPanelProps {
	className?: string;
	styles?: (StyleDeclarationValue | false)[];
	disabled?: boolean;
	onChangeDebounced?: () => void;
}

const DEBOUNCE_MS = 300;

export const LocationFiltersPanel: FC<LocationFiltersPanelProps> = ({
	className,
	styles = [],
	disabled = false,
	onChangeDebounced,
}) => {
	const { locationFilters } = useAdvancedFiltersContext();
	const { city, stateProvince, postalCode, setCity, setStateProvince, setPostalCode } = locationFilters;

	// Local state for immediate input feedback
	const [localCity, setLocalCity] = useState(city);
	const [localState, setLocalState] = useState(stateProvince);
	const [localZip, setLocalZip] = useState(postalCode);

	// Sync local state when filter changes externally (e.g., loading saved search)
	useEffect(() => {
		setLocalCity(city);
	}, [city]);

	useEffect(() => {
		setLocalState(stateProvince);
	}, [stateProvince]);

	useEffect(() => {
		setLocalZip(postalCode);
	}, [postalCode]);

	// Debounced callbacks to avoid excessive API calls while typing
	const debouncedSetCity = useDebounceCallback((value: string) => {
		setCity(value);
		onChangeDebounced?.();
	}, DEBOUNCE_MS);

	const debouncedSetState = useDebounceCallback((value: string) => {
		setStateProvince(value);
		onChangeDebounced?.();
	}, DEBOUNCE_MS);

	const debouncedSetZip = useDebounceCallback((value: string) => {
		setPostalCode(value);
		onChangeDebounced?.();
	}, DEBOUNCE_MS);

	const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (disabled) return;
		const value = e.target.value;
		setLocalCity(value);
		debouncedSetCity(value);
	};

	const handleStateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (disabled) return;
		const value = e.target.value;
		setLocalState(value);
		debouncedSetState(value);
	};

	const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (disabled) return;
		const value = e.target.value;
		setLocalZip(value);
		debouncedSetZip(value);
	};

	return (
		<div className={`${css(styleSheet.container, ...(styles?.filter(Boolean) || []))} ${className || ''}`}>
			<div className={css(styleSheet.fieldContainer)}>
				<label htmlFor='location-filter-city' className={css(styleSheet.fieldLabel)}>
					City
				</label>
				<input
					id='location-filter-city'
					type='text'
					className={css(styleSheet.input)}
					value={localCity}
					onChange={handleCityChange}
					placeholder='e.g., Boston'
					autoComplete='off'
					disabled={disabled}
				/>
			</div>
			<div className={css(styleSheet.fieldContainer)}>
				<label htmlFor='location-filter-state' className={css(styleSheet.fieldLabel)}>
					State/Province
				</label>
				<input
					id='location-filter-state'
					type='text'
					className={css(styleSheet.input)}
					value={localState}
					onChange={handleStateChange}
					placeholder='e.g., MA'
					autoComplete='off'
					disabled={disabled}
				/>
			</div>
			<div className={css(styleSheet.fieldContainer)}>
				<label htmlFor='location-filter-zip' className={css(styleSheet.fieldLabel)}>
					Zip/Postal Code
				</label>
				<input
					id='location-filter-zip'
					type='text'
					className={css(styleSheet.input)}
					value={localZip}
					onChange={handleZipChange}
					placeholder='e.g., 02101'
					autoComplete='off'
					disabled={disabled}
				/>
			</div>
		</div>
	);
};
